import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { createRef, FocusEventHandler, FunctionComponent } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import {
  brandAtom,
  modelAtom,
  slugAtom,
  versionAtom,
} from '../../../../../hooks/use-picker-selection';
import { fetchVersions } from '../../../../../services/vehicles';
import Select, {
  CustomClearIndicator,
  CustomDownChevron,
  CustomMenuWithoutMobile,
  CustomOptionModelsVersions,
  CustomSingleValueModelsVersions,
} from '../../../../ui/inputs/custom-selector';

export const versionRef = createRef();

const loadVersions = async ({ queryKey: [_, locale, slug] }) => {
  const data = await fetchVersions({ locale, slug });

  return Object.entries(data.children).map(([key, value]) => ({
    label: key,
    options: value.map((item) => ({
      value: item.slug,
      label: item.name,
      start_date: item.start_date,
      end_date: item.end_date || new Date().getFullYear(),
    })),
  }));
};

const VersionSelect: FunctionComponent<{
  onFocus?: FocusEventHandler;
  register?: UseFormRegisterReturn;
  error?: Array<string>;
  setValue?: any;
}> = ({ onFocus, register, error, setValue }) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const [brand] = useAtom(brandAtom);
  const [model] = useAtom(modelAtom);
  const [slug] = useAtom(slugAtom);
  const [version, setVersion] = useAtom(versionAtom);

  const { data: versions, isLoading: isVersionsLoading } = useQuery(
    ['search-brands', locale, model?.value],
    loadVersions,
    {
      enabled: !!model,
    }
  );

  if (model && !model?.have_versions) {
    return null;
  }

  return (
    <Select
      {...register}
      ref={versionRef}
      onFocus={onFocus}
      aria-label={t('vehicle-picker:chooseVersion')}
      id="version-input"
      openMenuOnFocus
      value={version}
      isDisabled={!model}
      isLoading={!!model && isVersionsLoading}
      loadingMessage={() => t('common:loading')}
      options={versions}
      components={{
        Option: CustomOptionModelsVersions,
        SingleValue: CustomSingleValueModelsVersions,
        DropdownIndicator: CustomDownChevron,
        MenuList: CustomMenuWithoutMobile,
        ClearIndicator: CustomClearIndicator,
      }}
      onChange={(item) => {
        setVersion(item);
        setValue('slug', { brand: brand, model: model, version: item });
      }}
      placeholder={t('vehicle-picker:chooseVersion')}
      error={error}
      isClearable
    />
  );
};

export default VersionSelect;
